import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum TrackstarReturnProvider {
  SHIPSTREAM = "shipstream",
  BLUEBOX = "bluebox",
  SHIPMONK = "shipmonk",
  DEPOSCO = "deposco",
  NIMBLE = "nimble",
  WHIPLASH_RYDER = "whiplash",
  CIRRO = "cirro",
}

export const TrackstarReturnProviderMerchantNames: Record<
  TrackstarReturnProvider,
  string
> = {
  [TrackstarReturnProvider.SHIPSTREAM]: "ShipStream",
  [TrackstarReturnProvider.BLUEBOX]: "BlueBox",
  [TrackstarReturnProvider.SHIPMONK]: "ShipMonk",
  [TrackstarReturnProvider.DEPOSCO]: "Deposco",
  [TrackstarReturnProvider.NIMBLE]: "Nimble",
  [TrackstarReturnProvider.WHIPLASH_RYDER]: "Whiplash Ryder",
  [TrackstarReturnProvider.CIRRO]: "Cirro",
};

export const getMerchantMessage = (errorCode: ReturnIntegrationErrorCode) => {
  switch (errorCode) {
    case InmarIntegrationErrorCode.INMAR_MISSING_ITEM_ID:
      return "One or more products in this return are missing a required UPC or SKU for Inmar. Please verify that all products have the correct identifiers configured and retry using the actions menu below, or create the RMA manually in Inmar.";
    case InmarIntegrationErrorCode.INMAR_NO_INTEGRATION:
      return "The Inmar integration is not configured. Please set up the Inmar integration in your settings and retry using the actions menu below.";
    case InmarIntegrationErrorCode.INMAR_NO_SHIPMENTS:
      return "No shipments are going to an Inmar warehouse for this return. Please verify the return address and retry using the actions menu below, or create the RMA manually in Inmar.";
    case InmarIntegrationErrorCode.INMAR_NO_BOX_BARCODES:
      return "No box barcodes (tracking codes) were found for this return. Shipping labels may not have been generated yet. Please wait for labels to be created and retry using the actions menu below.";
    case GlobaleIntegrationErrorCode.GLOBALE_MISSING_ORDER_NUMBER:
      return "Global-e requires a merchant order number, but none was found on the order. Please verify the order has a Shopify order number and retry using the actions menu below, or create the RMA manually in Global-e.";
    case GlobaleIntegrationErrorCode.GLOBALE_MISSING_SKU:
      return "One or more products in this return are missing a SKU required by Global-e. Please verify that all products have a valid SKU configured and retry using the actions menu below, or create the RMA manually in Global-e.";
    case GlobaleIntegrationErrorCode.GLOBALE_NO_PRODUCTS:
      return "This return has no products to send to Global-e. Please verify the return has at least one returned product and retry using the actions menu below.";
    case GlobaleIntegrationErrorCode.GLOBALE_API_FAILURE:
      return "Global-e rejected the request to create the return. Please retry using the actions menu below, or create the RMA manually in Global-e.";
    case BlueCherryIntegrationErrorCode.BLUE_CHERRY_GENERAL_ERROR:
      return "The BlueCherry integration failed to create the RMA. Please retry using the actions menu below, or create the RMA manually in your BlueCherry system.";
    case BlueCherryIntegrationErrorCode.BLUE_CHERRY_NO_INTEGRATION:
      return "The BlueCherry integration is not configured. Please set up the BlueCherry integration in your settings and retry using the actions menu below.";
    case MintsoftIntegrationErrorCode.MINTSOFT_GENERAL_ERROR:
      return "Failed to create return in Mintsoft";
    case MintsoftIntegrationErrorCode.MINTSOFT_NO_INTEGRATION:
      return "Mintsoft integration is not set up";
    case MintsoftIntegrationErrorCode.MINTSOFT_AUTH_FAILED:
      return "Mintsoft authentication failed";
    case MintsoftIntegrationErrorCode.MINTSOFT_WAREHOUSE_NOT_FOUND:
      return "No Mintsoft warehouse is mapped to this return's location";
    case MintsoftIntegrationErrorCode.MINTSOFT_CLIENT_ID_MISSING:
      return "Mintsoft client ID is missing from the integration";
    case Aims360IntegrationErrorCode.AIMS360_GENERAL_ERROR:
      return "The Aims360 integration failed to create the RMA. Please retry using the actions menu below, or create the RMA manually in your Aims360 system.";
    case Aims360IntegrationErrorCode.AIMS360_NO_INVOICES_FOUND:
      return "No Aims360 invoices were found for this order. Please verify that the order has been invoiced in Aims360, or create the RMA manually in your Aims360 system.";
    case Aims360IntegrationErrorCode.AIMS360_NO_AVAILABLE_QUANTITY:
      return "No available quantity was found for one or more items in this return. The items may have already been returned in Aims360. Please verify the available quantity in Aims360, or create the RMA manually in your Aims360 system.";
    case FlexportIntegrationErrorCode.FLEXPORT_TRACKING_CODE_NOT_FOUND:
      return "Flexport could not find the tracking code for this shipment. Please retry using the actions menu below, or create the RMA manually in your Flexport system.";
    case FlexportIntegrationErrorCode.FLEXPORT_CARRIER_NOT_FOUND:
      return "Flexport could not find the carrier for this shipment. Please retry using the actions menu below, or create the RMA manually in your Flexport system.";
    case FlexportIntegrationErrorCode.FLEXPORT_INVALID_LOCATION:
      return "The return location is invalid or missing in your shipping and integration settings. Please add the location and retry using the actions menu below, or create the RMA manually in your Flexport system.";
    case FlexportIntegrationErrorCode.FLEXPORT_DUPLICATE_ENTRY:
      return "This return already exists in Flexport with the same tracking code. Check your Flexport system to confirm the return was created successfully, then dismiss this error.";
    case FlexportIntegrationErrorCode.FLEXPORT_PRODUCT_NOT_FOUND:
      return "One or more products in this return were not found in Flexport. Please retry using the actions menu below, excluding any non-physical items, or create the RMA manually in your Flexport system.";
    case FlexportIntegrationErrorCode.FLEXPORT_BAD_REQUEST:
      return "Flexport rejected the request to create the RMA, often due to non-physical products. Please retry using the actions menu below, excluding non-physical items, or create the RMA manually in your Flexport system.";
    case FlexportIntegrationErrorCode.FLEXPORT_TOKEN_INVALID:
      return "Flexport rejected the stored bearer token (likely revoked or rotated). Please update the Flexport API token in your settings and retry using the actions menu below, or create the RMA manually in your Flexport system.";
    case NetsuiteIntegrationErrorCode.NETSUITE_AMBIGUOUS_ORDER:
      return "The order lookup found multiple matching NetSuite transactions. Correct the order references or lookup settings so only the correct transaction matches. Then retry using the actions menu below.";
    case NetsuiteIntegrationErrorCode.NETSUITE_SALES_ORDER_NOT_FOUND:
      return "The sales order was not found in NetSuite. Please verify the order exists and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_RETURN_AUTHORIZATION_CANNOT_BE_INITIALIZED:
      return "NetSuite could not initialize the return authorization, likely because the sales order is not yet fulfilled. Please fulfill the order and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_RETURN_AUTHORIZATION_MUST_HAVE_LINE_ITEMS:
      return "No valid line items were found for the NetSuite return authorization. Please verify SKUs and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_END_OF_GROUP_LINE_DELETE_ERROR:
      return "NetSuite encountered a group line error when creating the RMA. See this link for details: https://docs.oracle.com/en/cloud/saas/netsuite/ns-online-help/section_163306924686.html Retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_THIS_RECORD_ALREADY_EXISTS_ERROR:
      return "This return authorization already exists in NetSuite. Check your NetSuite system to confirm it was created successfully, then dismiss this error.";
    case NetsuiteIntegrationErrorCode.NETSUITE_INVALID_FIELD_VALUE_ERROR:
      return "NetSuite rejected invalid field values, likely due to inactive or matrix items. Please correct them and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_INVALID_LOCATION:
      return "NetSuite rejected the location on this return authorization. If this was an in-store return, check the NetSuite location ID mapped to its POS location in your point of sale settings \u2014 it must be an active location in the same NetSuite subsidiary as the customer on the return. Correct the mapping and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_INVALID_LOGIN_ATTEMPT_ERROR:
      return "Access to NetSuite has been lost. Please re-authenticate and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_UNEXPECTED_SUITE_SCRIPT_ERROR:
      return "NetSuite encountered an unexpected SuiteScript error. Please contact support and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_SALES_ORDER_MUST_BE_FULFILLED_ERROR:
      return "NetSuite requires the sales order to be fulfilled before creating a return authorization. Please fulfill the order and retry using the actions menu below.";
    case NetsuiteIntegrationErrorCode.NETSUITE_PERMISSION_VIOLATION_LISTS_TAX:
      return "NetSuite requires permission (lists -> tax details tab) to create a return authorization. Please check your NetSuite permissions and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_RMA_SOURCE_UNAVAILABLE:
      return "None of the NetSuite records this integration is configured to build return authorizations from exists for this order yet, for example the invoice has not been created. Redo keeps retrying for several days; once the record exists the RMA is created automatically, or retry using the actions menu below.";
    case NetsuiteIntegrationErrorCode.NETSUITE_INVENTORY_DETAIL_QUANTITY_ERROR:
      return "NetSuite inventory detail quantity error. The Sales Order has multiple line items with the same SKU, and the system cannot determine which line to apply the return to. Manual RMA creation is required.";
    case NetsuiteIntegrationErrorCode.NETSUITE_ITEM_NOT_FOUND_FOR_SKU:
      return "One or more returned products have SKUs that do not match any active item in NetSuite. Please add or activate the item(s) in NetSuite and retry using the actions menu below, or create the RMA manually in NetSuite.";
    case NetsuiteIntegrationErrorCode.NETSUITE_CUSTOMER_RESOLUTION_FAILED:
      return "The customer for this return could not be found or created in NetSuite. Please verify the return has a customer email and that Redo's NetSuite role can view and create customers, then retry using the actions menu below, or create the RMA manually in NetSuite.";
    case SfomIntegrationErrorCode.SFOM_DIDNT_RECEIVE_RMA_ID:
      return "SFOM did not return an RMA ID. Please retry using the actions menu below, or create the RMA manually in your SFOM system.";
    case SellercloudIntegrationErrorCode.SELLERCLOUD_NO_INTEGRATION:
      return "The Sellercloud integration is not configured. Please set up the Sellercloud integration in your settings and retry using the actions menu below.";
    case SellercloudIntegrationErrorCode.SELLERCLOUD_AUTHENTICATION_ERROR:
      return "Sellercloud rejected the stored credentials. Please verify the server URL, username, and password in your Sellercloud integration settings and retry using the actions menu below, or create the RMA manually in Sellercloud.";
    case SellercloudIntegrationErrorCode.SELLERCLOUD_NO_ORDERS_FOUND:
      return "No Sellercloud order was found for this Shopify order. Please verify the order exists in Sellercloud and retry using the actions menu below, or create the RMA manually in Sellercloud.";
    case SellercloudIntegrationErrorCode.SELLERCLOUD_PERMISSION_DENIED:
      return "The Sellercloud API user is not allowed to create RMAs. Please grant the API user RMA-creation permission and a company assignment in Sellercloud, then retry using the actions menu below, or create the RMA manually in Sellercloud.";
    case SellercloudIntegrationErrorCode.SELLERCLOUD_NO_RETURN_ID:
      return "Sellercloud did not return an RMA ID after creation. Please check your Sellercloud system to verify if the RMA was created, or create the RMA manually in Sellercloud.";
    case SellercloudIntegrationErrorCode.SELLERCLOUD_API_ERROR:
      return "The Sellercloud API returned an error. Please retry using the actions menu below, or create the RMA manually in Sellercloud.";
    case RyderIntegrationErrorCode.RYDER_NO_INTEGRATION:
      return "The Ryder integration is not configured. Please set up the Ryder integration in your settings and retry using the actions menu below.";
    case RyderIntegrationErrorCode.RYDER_MISSING_REFRESH_TOKEN:
      return "Ryder access has been lost. Please re-authorize the Ryder integration in your settings and retry using the actions menu below, or create the RMA manually in Ryder.";
    case RyderIntegrationErrorCode.RYDER_REFRESH_TOKEN_INVALID:
      return "Ryder rejected the stored refresh token (likely revoked or expired). Please re-authorize the Ryder integration in your settings and retry using the actions menu below, or create the RMA manually in Ryder.";
    case RyderIntegrationErrorCode.RYDER_NO_ORDERS_FOUND:
      return "No Ryder order was found for this Shopify order. Please verify the order exists in Ryder and retry using the actions menu below, or create the RMA manually in Ryder.";
    case RyderIntegrationErrorCode.RYDER_NO_WAREHOUSES:
      return "No Ryder warehouses were found. Please verify your Ryder account has warehouses configured and retry using the actions menu below, or create the RMA manually in Ryder.";
    case RyderIntegrationErrorCode.RYDER_WAREHOUSE_NOT_MATCHED:
      return "No Ryder warehouse could be matched to the return address. Please verify that your Ryder warehouses are configured for returns and that the return address matches a warehouse, or create the RMA manually in Ryder.";
    case RyderIntegrationErrorCode.RYDER_NO_RETURN_ID:
      return "Ryder did not return an RMA ID after creation. Please check your Ryder system to verify if the return was created, or create the RMA manually in Ryder.";
    case RyderIntegrationErrorCode.RYDER_DUPLICATE_REFERENCE:
      return "This return already exists in Ryder. Please check your Ryder system to confirm it was created successfully, then dismiss this error.";
    case RyderIntegrationErrorCode.RYDER_NO_RETURN_ITEMS:
      return "No returnable items were found for this return in Ryder. Please verify the products have valid SKUs and retry using the actions menu below, or create the RMA manually in Ryder.";
    case ShipbobIntegrationErrorCode.SHIPBOB_NO_ORDERS_FOUND_FOR_SHOPIFY_ORDER_ERROR:
      return "No ShipBob order was found for this Shopify order. Please update the reference ID and retry using the actions menu below, or create the RMA manually in ShipBob.";
    case ShipbobIntegrationErrorCode.SHIPBOB_NO_FULFILLMENT_CENTERS:
      return "No ShipBob fulfillment centers were found. Please verify your ShipBob account has fulfillment centers configured and retry using the actions menu below, or create the RMA manually in ShipBob.";
    case ShipbobIntegrationErrorCode.SHIPBOB_NO_REDO_CHANNEL:
      return "No Redo channel was found in ShipBob. Please ensure the Redo channel is set up in your ShipBob account and retry using the actions menu below, or create the RMA manually in ShipBob.";
    case ShipbobIntegrationErrorCode.SHIPBOB_NO_REFERENCE_IDS:
      return "No valid product reference IDs were found for this return. Please verify the products have valid SKUs or variant IDs and retry using the actions menu below, or create the RMA manually in ShipBob.";
    case ShipbobIntegrationErrorCode.SHIPBOB_DUPLICATE_REFERENCE_ID:
      return "This return already exists in ShipBob. Please check your ShipBob system to confirm it was created successfully, then dismiss this error.";
    case ShipbobIntegrationErrorCode.SHIPBOB_MISSING_REFRESH_TOKEN:
      return "ShipBob access has been lost. Please re-authorize the ShipBob integration in your settings and retry using the actions menu below, or create the RMA manually in ShipBob.";
    case ShipbobIntegrationErrorCode.SHIPBOB_REFRESH_TOKEN_INVALID:
      return "ShipBob rejected the stored refresh token (likely revoked or expired). Please re-authorize the ShipBob integration in your settings and retry using the actions menu below, or create the RMA manually in ShipBob.";
    case ShipbobIntegrationErrorCode.SHIPBOB_NO_INTEGRATION:
      return "The ShipBob integration is not configured. Please set up the ShipBob integration in your settings and retry using the actions menu below.";
    case ShipbobIntegrationErrorCode.SHIPBOB_NO_RETURN_ID:
      return "ShipBob did not return an RMA ID after creation. Please check your ShipBob system to verify if the return was created, or create the RMA manually in ShipBob.";
    case ShipbobIntegrationErrorCode.SHIPBOB_NO_RETURN_FOUND_BY_REFERENCE_ID:
      return "A duplicate reference ID error occurred but the existing return could not be found in ShipBob. Please check your ShipBob system or create the RMA manually.";
    case ShipbobIntegrationErrorCode.SHIPBOB_FULFILLMENT_CENTER_NOT_MATCHED:
      return "No ShipBob fulfillment center could be matched to the return address. Please verify that your ShipBob fulfillment centers are configured for returns and that the return address matches a fulfillment center, or create the RMA manually in ShipBob.";
    case ApparelMagicIntegrationErrorCode.APPAREL_MAGIC_ORDER_NOT_FOUND:
      return "No Apparel Magic order was found for this return. Please verify the order and retry using the actions menu below, or create the RMA manually in Apparel Magic.";
    case ApparelMagicIntegrationErrorCode.APPAREL_MAGIC_RA_NOT_DEFINED:
      return "The Apparel Magic RA number is not defined for this return. Please retry using the actions menu below, or create the RMA manually in Apparel Magic.";
    case ApparelMagicIntegrationErrorCode.APPAREL_MAGIC_MULTIPLE_ORDERS:
      return "This return contains multiple orders, which is not supported by Apparel Magic. Please create separate RMAs manually in Apparel Magic.";
    case ApparelMagicIntegrationErrorCode.APPAREL_MAGIC_NO_ITEMS_TO_RETURN:
      return "No returnable items were found in Apparel Magic for this return. Please retry using the actions menu below, or create the RMA manually in Apparel Magic.";
    case ApparelMagicIntegrationErrorCode.APPAREL_MAGIC_INSUFFICIENT_QUANTITY:
      return "The quantity of items in Apparel Magic is insufficient for this return. Please verify the quantity and retry using the actions menu below, or create the RMA manually in Apparel Magic.";
    case ApparelMagicIntegrationErrorCode.APPAREL_MAGIC_UNAUTHORIZED_ERROR:
      return "Access to Apparel Magic has been lost or the API token is invalid. Please re-authenticate and retry using the actions menu below, or create the RMA manually in Apparel Magic.";
    case BrightpearlIntegrationErrorCode.BRIGHTPEARL_SALES_ORDER_ROW_NOT_FOUND:
      return "No Brightpearl sales order row was found for the returned products. Please verify the products and retry using the actions menu below, or create the RMA manually in Brightpearl.";
    case BrightpearlIntegrationErrorCode.BRIGHTPEARL_ORIGINAL_SALES_ORDER_NOT_FOUND:
      return "The original sales order was not found in Brightpearl. Please ensure it exists and retry using the actions menu below, or create it manually.";
    case CloudxIntegrationErrorCode.CLOUDX_ITEM_MASTER_ERROR:
      return "Bergen encountered an item master error. Please verify SKUs and retry using the actions menu below, or create the receiving ticket manually in Bergen.";
    case CloudxIntegrationErrorCode.CLOUDX_STATE_ERROR:
      return "Bergen requires a valid US state code for returns. Please retry using the actions menu below, or create the receiving ticket manually in Bergen.";
    case CloudxIntegrationErrorCode.CLOUDX_UPC_SKU_NOT_FOUND:
      return "One or more products in this return are missing a UPC or SKU. Please verify that all products have a valid UPC/SKU configured in Shopify, or create the receiving ticket manually in Bergen.";
    case CloudxIntegrationErrorCode.CLOUDX_REFRESH_TOKEN_INVALID:
      return "Bergen rejected the stored credentials (likely revoked or expired). Please re-authorize the Bergen integration in your settings and retry using the actions menu below, or create the receiving ticket manually in Bergen.";
    case ExtensivIntegrationErrorCode.EXTENSIV_AUTHENTICATION_ERROR:
      return "Access to Extensiv has been lost or the API credentials are invalid. Please re-authenticate and retry using the actions menu below, or create the receiver manually in Extensiv.";
    case ExtensivIntegrationErrorCode.EXTENSIV_BAD_REQUEST:
      return "Extensiv rejected the request to create the receiver. Please verify all product SKUs and configuration, then retry using the actions menu below, or create the receiver manually in Extensiv.";
    case ExtensivIntegrationErrorCode.EXTENSIV_DUPLICATE_ENTRY:
      return "This return already exists in Extensiv. Please check your Extensiv system to confirm it was created successfully, then dismiss this error.";
    case ShipheroIntegrationErrorCode.SHIPHERO_ORDER_NOT_FOUND:
      return "No ShipHero order was found for this Shopify order. Please verify and retry using the actions menu below, or create the RMA manually in ShipHero.";
    case ShipheroIntegrationErrorCode.SHIPHERO_SKU_MISSING_ON_ORDER:
      return "One or more SKUs were not found on the original ShipHero order. Please verify and retry using the actions menu below, or create the RMA manually in ShipHero.";
    case ShipheroIntegrationErrorCode.SHIPHERO_ACCOUNT_CANCELED:
      return "The ShipHero account has been canceled or is inactive. Please verify your ShipHero account status and re-authenticate if needed, or create the RMA manually in ShipHero.";
    case ShipheroIntegrationErrorCode.SHIPHERO_NO_ACCESS_TOKEN:
      return "Access to ShipHero has been lost. The refresh token is no longer accepted by ShipHero — this typically means the third-party developer user was removed in ShipHero or the account became inactive. Please reconnect the ShipHero integration in your settings, or create the RMA manually in ShipHero.";
    case TrackstarIntegrationErrorCode.ORDER_NOT_FOUND:
      return "The order was not found in the WMS. Please verify and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.ORDER_ID_NOT_FOUND_AFTER_CALLING_TRACKSTAR_ORDER_API:
      return "No order ID was found for this return in the WMS. Please verify the order exists and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.ORDER_ITEM_NOT_FOUND:
      return "One or more items from the return were not found in the WMS. Please verify and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.WHIPLASH_RYDER_INVENTORY_ITEM_NOT_FOUND:
      return "One or more inventory items were not found in Whiplash Ryder WMS. Make sure that inventory for the SKUs in this return are properly imported into Whiplash Ryder WMS. Please verify and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.WHIPLASH_RYDER_SKU_NOT_FOUND:
      return "One or more SKUs in this return were not found on the Whiplash Ryder WMS order. Please ensure the SKUs exist on the corresponding WMS order and are properly configured, then retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.RETURN_ALREADY_EXISTS:
      return "This return already exists in the WMS. Please check your system to confirm it was created successfully, then dismiss this error.";
    case TrackstarIntegrationErrorCode.NO_WAREHOUSE_FOUND_FOR_SHIPMENT:
      return "No warehouse was found for this return shipment. Please add the warehouse and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.NO_ORDER_ID_FOUND_FOR_RETURN:
      return "No order ID was found for this return in the WMS. Please verify the order exists and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.ORDER_NOT_FOUND_FOR_CUSTOMER_NUMBER_OR_PO_NUMBER:
      return "The order was not found in the WMS. Please verify the order exists and retry using the actions menu below, or create the RMA manually in your WMS.";
    case TrackstarIntegrationErrorCode.SHIPSTREAM_DELIVERY_ALREADY_EXISTS:
      return "The ShipStream delivery already exists. Please check your system to confirm it was created successfully, then dismiss this error.";
    case TrackstarIntegrationErrorCode.SHIPSTREAM_SKU_NOT_FOUND:
      return "One or more SKUs in this return were not found in ShipStream. Please ensure the SKUs exist on the associated WMS order and retry using the actions menu below, or create the RMA manually in ShipStream.";
    case TrackstarIntegrationErrorCode.RYDER_ORDER_NOT_YET_SHIPPED:
      return "Unable to create a return because the order has not yet shipped in RyderShip. The order must be marked as shipped in RyderShip before a return can be created. Please go and mark the order as shipped and retry using the actions menu below.";
    case TrackstarIntegrationErrorCode.DEPOSCO_DUPLICATE_ENTRY:
      return "This return already exists in Deposco. Please check your Deposco system to confirm it was created successfully, then dismiss this error.";
    case TrackstarIntegrationErrorCode.DEPOSCO_ORDER_NOT_FOUND:
      return "The order was not found in Deposco. Please verify the order exists in your Deposco system and retry using the actions menu below, or create the RMA manually in Deposco.";
    case TrackstarIntegrationErrorCode.DEPOSCO_INVALID_CREDENTIALS:
      return "Deposco rejected the username and password stored in Trackstar. Please update your Deposco credentials in Trackstar and retry using the actions menu below, or create the RMA manually in Deposco.";
    case DeposcoIntegrationErrorCode.REFRESH_TOKEN_INVALID:
      return "Deposco rejected the stored refresh token (likely revoked or expired). Please re-authorize the Deposco integration in your settings and retry using the actions menu below, or create the RMA manually in Deposco.";
    case MasonhubIntegrationErrorCode.MASONHUB_NO_INTEGRATION:
      return "The MasonHub integration is not configured. Please set up the MasonHub integration in your settings and retry using the actions menu below.";
    case MasonhubIntegrationErrorCode.MASONHUB_AUTHENTICATION_ERROR:
      return "Access to MasonHub has been lost or the API token is invalid. Please re-authenticate and retry using the actions menu below, or create the RMA manually in MasonHub.";
    case MasonhubIntegrationErrorCode.MASONHUB_BAD_REQUEST:
      return "MasonHub rejected the request to create the RMA. Please verify all product SKUs and configuration, then retry using the actions menu below, or create the RMA manually in MasonHub.";
    case MasonhubIntegrationErrorCode.MASONHUB_DUPLICATE_ENTRY:
      return "This return already exists in MasonHub. Please check your MasonHub system to confirm it was created successfully, then dismiss this error.";
    case MasonhubIntegrationErrorCode.MASONHUB_SKU_NOT_FOUND:
      return "One or more product SKUs in this return were not found in MasonHub. Please verify that all products have valid SKUs configured, or create the RMA manually in MasonHub.";
    case MasonhubIntegrationErrorCode.MASONHUB_NO_ITEMS:
      return "No items on this return have SKUs that can be sent to MasonHub. Please verify the return contains products with SKUs, or create the RMA manually in MasonHub.";
    case MasonhubIntegrationErrorCode.MASONHUB_API_ERROR:
      return "The MasonHub API returned an error. Please retry using the actions menu below, or create the RMA manually in MasonHub.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_NO_INTEGRATION:
      return "The ShipMonk integration is not configured. Please set up the ShipMonk integration in your settings and retry using the actions menu below.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_DUPLICATE_ENTRY:
      return "This return already exists in ShipMonk. Please check your ShipMonk system to confirm it was created successfully, then dismiss this error.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_BAD_REQUEST:
      return "ShipMonk rejected the request to create the RMA. Please verify all product SKUs and configuration, then retry using the actions menu below, or create the RMA manually in ShipMonk.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_SKU_NOT_FOUND:
      return "One or more product SKUs in this return were not found in ShipMonk. Please verify that all products have valid SKUs configured, or create the RMA manually in ShipMonk.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_WAREHOUSE_NOT_CONFIGURED:
      return "No ShipMonk warehouse is configured for this integration. Please configure the warehouse in your ShipMonk integration settings and retry using the actions menu below.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_STORE_NOT_CONFIGURED:
      return "No ShipMonk store is configured for this integration. Please configure the store in your ShipMonk integration settings and retry using the actions menu below.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_AUTHENTICATION_ERROR:
      return "Access to ShipMonk has been lost or the API key is invalid. Please re-authenticate and retry using the actions menu below, or create the RMA manually in ShipMonk.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_NO_WAREHOUSES:
      return "No ShipMonk warehouses were found. Please verify your ShipMonk account has warehouses configured and retry using the actions menu below, or create the RMA manually in ShipMonk.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_WAREHOUSE_NOT_MATCHED:
      return "No ShipMonk warehouse could be matched to the return address. Please verify that your ShipMonk warehouses are configured for returns and that the return address matches a warehouse, or create the RMA manually in ShipMonk.";
    case ShipmonkIntegrationErrorCode.SHIPMONK_ORDER_NOT_FOUND:
      return "No ShipMonk order was found for this return. Please verify the order exists in ShipMonk and retry using the actions menu below, or create the RMA manually in ShipMonk.";
    case EcwidIntegrationErrorCode.ECWID_NO_INTEGRATION:
      return "The Ecwid integration is not configured. Please set up the Ecwid integration in your settings and retry using the actions menu below.";
    case EcwidIntegrationErrorCode.ECWID_MISSING_SHIPPING_ADDRESS:
      return "Ecwid requires a shipping address, but none was found on this return. Please verify the return has a shipping address and retry using the actions menu below.";
    case EcwidIntegrationErrorCode.ECWID_NO_ITEMS_TO_SUBMIT:
      return "No items on this return matched the configured Ecwid return reasons, so no order was created in Ecwid.";
    case EcwidIntegrationErrorCode.ECWID_AUTHENTICATION_ERROR:
      return "Access to Ecwid has been lost or the API token is invalid. Please re-authenticate in your settings and retry using the actions menu below.";
    case EcwidIntegrationErrorCode.ECWID_REQUEST_FAILED:
      return "Ecwid rejected the request to create the order. Please retry using the actions menu below, or create the order manually in Ecwid.";
    case EcwidIntegrationErrorCode.ECWID_INVALID_RESPONSE:
      return "Ecwid returned an unexpected response when creating the order. Please check Ecwid to verify whether the order was created, then retry or dismiss this error.";
    case DeposcoIntegrationErrorCode.NO_INTEGRATION:
      return "The Deposco integration is not configured. Please set up the Deposco integration in your settings and retry using the actions menu below.";
    case DeposcoIntegrationErrorCode.NO_ORDER:
      return "No Deposco order was found for this return. Please verify the order exists in Deposco and retry using the actions menu below, or create the RMA manually in Deposco.";
    case DeposcoIntegrationErrorCode.NO_FACILITY_MATCH:
      return "No Deposco facility could be matched to the return address. Please verify that your Deposco facilities are configured for returns and that the return address matches a facility, or create the RMA manually in Deposco.";
    case DeposcoIntegrationErrorCode.API_ERROR:
      return "The Deposco API returned an error. Please retry using the actions menu below, or create the RMA manually in Deposco.";
    case ShipedgeIntegrationErrorCode.SHIPEDGE_DUPLICATE_ENTRY:
      return "This return already exists in Shipedge. Please check your Shipedge system to confirm it was created successfully, then dismiss this error.";
    case ShipedgeIntegrationErrorCode.SHIPEDGE_BAD_REQUEST:
      return "Shipedge rejected the request to create the RMA. Please verify all product SKUs exist in Shipedge and retry using the actions menu below, or create the RMA manually in Shipedge.";
    case ShipedgeIntegrationErrorCode.SHIPEDGE_AUTHENTICATION_ERROR:
      return "Access to Shipedge has been lost or the API credentials are invalid. Please re-authenticate and retry using the actions menu below, or create the RMA manually in Shipedge.";
    case ShipedgeIntegrationErrorCode.SHIPEDGE_ORDER_NOT_FOUND:
      return "The original order was not found in Shipedge. Please verify the order exists in Shipedge and retry using the actions menu below, or create the RMA manually in Shipedge.";
    case UnknownErrorCode.UNKNOWN_ERROR:
      return "An unexpected error occurred during RMA creation. Please retry using the actions menu below, or create the RMA manually in your WMS. Contact support if this persists.";
    case GeneralErrorCode.LABEL_REQUIRED:
      return "A return shipping label is required before the RMA can be created. Please generate a return label and retry using the actions menu below, or create the RMA manually in your system.";
    case GeneralErrorCode.RMA_FUNCTION_FAILED:
      return "The RMA function for this integration failed, so the RMA was not sent. Please fix the function or turn it off in the integration's settings, then retry using the actions menu below.";
    case Cin7OmniIntegrationErrorCode.CIN7_OMNI_NO_INTEGRATION:
      return "The Cin7 Omni integration is not configured. Please set up the Cin7 Omni integration in your settings and retry using the actions menu below.";
    case Cin7OmniIntegrationErrorCode.CIN7_OMNI_INVALID_ORDER:
      return "The order data for this return could not be read for Cin7 Omni. Please retry using the actions menu below, or create the credit note manually in Cin7 Omni.";
    case Cin7OmniIntegrationErrorCode.CIN7_OMNI_NO_ORDERS_FOUND:
      return "No matching Cin7 Omni sales order was found for this return. Please verify the order exists in Cin7 Omni and retry using the actions menu below, or create the credit note manually in Cin7 Omni.";
    case Cin7OmniIntegrationErrorCode.CIN7_OMNI_NO_RETURN_ITEMS:
      return "This return has no items to credit in Cin7 Omni. Please verify the return has at least one returned product and retry using the actions menu below.";
    case Cin7OmniIntegrationErrorCode.CIN7_OMNI_NO_RETURN_ID:
      return "Cin7 Omni did not return a credit note ID. Please check Cin7 Omni to confirm whether the credit note was created, then retry or dismiss this error.";
    case Cin7OmniIntegrationErrorCode.CIN7_OMNI_API_ERROR:
      return "Cin7 Omni rejected the request to create the credit note. Please retry using the actions menu below, or create the credit note manually in Cin7 Omni.";
    case Indigo8IntegrationErrorCode.INDIGO8_NO_INTEGRATION:
      return "The Indigo8 integration is not configured. Please set up the Indigo8 integration in your settings and retry using the actions menu below.";
    case Indigo8IntegrationErrorCode.INDIGO8_AUTHENTICATION_ERROR:
      return "Indigo8 rejected the API key. Please verify the API key in your Indigo8 integration settings and retry using the actions menu below.";
    case Indigo8IntegrationErrorCode.INDIGO8_VALIDATION_ERROR:
      return "Indigo8 rejected the return details. Please verify the order, product and return reason data, then retry using the actions menu below, or create the RMA manually in Indigo8.";
    case Indigo8IntegrationErrorCode.INDIGO8_NO_RECORDS_FOUND:
      return "Indigo8 could not find matching product records for this return. Please verify the products exist in Indigo8, then retry using the actions menu below, or create the RMA manually in Indigo8.";
    case Indigo8IntegrationErrorCode.INDIGO8_TENANT_MISMATCH:
      return "Indigo8 accepted this return into a different store than the one configured. Please verify the store URL in your Indigo8 integration settings before retrying, and check Indigo8 for an incorrectly created return.";
    case Indigo8IntegrationErrorCode.INDIGO8_API_ERROR:
      return "The Indigo8 API returned an error. Please retry using the actions menu below, or create the RMA manually in Indigo8.";
    case AstroIntegrationErrorCode.ASTRO_NO_INTEGRATION:
      return "The Astro integration is not configured. Please set up the Astro integration in your settings and retry using the actions menu below.";
    case AstroIntegrationErrorCode.ASTRO_AUTHENTICATION_ERROR:
      return "Astro rejected the API token. Please verify the token in your Astro integration settings and retry using the actions menu below.";
    case AstroIntegrationErrorCode.ASTRO_VALIDATION_ERROR:
      return "Astro rejected the return details. Please verify the order and product SKUs match the articles in Astro, then retry using the actions menu below, or create the return manually in Astro.";
    case AstroIntegrationErrorCode.ASTRO_API_ERROR:
      return "The Astro API returned an error. Please retry using the actions menu below, or create the return manually in Astro.";
    case ControlPortIntegrationErrorCode.CONTROLPORT_NO_INTEGRATION:
      return "The ControlPort integration is not configured. Please set up the ControlPort integration in your settings and retry using the actions menu below.";
    case ControlPortIntegrationErrorCode.CONTROLPORT_AUTHENTICATION_ERROR:
      return "ControlPort rejected the API key. Please verify the API key in your ControlPort integration settings and retry using the actions menu below.";
    case ControlPortIntegrationErrorCode.CONTROLPORT_VALIDATION_ERROR:
      return "ControlPort rejected the return details. Please verify the order, product and return reason data, then retry using the actions menu below, or create the RMA manually in ControlPort.";
    case ControlPortIntegrationErrorCode.CONTROLPORT_NO_RECORDS_FOUND:
      return "ControlPort could not find matching records for this return. Please verify the order and products exist in ControlPort, then retry using the actions menu below, or create the RMA manually in ControlPort.";
    case ControlPortIntegrationErrorCode.CONTROLPORT_API_ERROR:
      return "The ControlPort API returned an error. Please retry using the actions menu below, or create the RMA manually in ControlPort.";
    case OdooIntegrationErrorCode.ODOO_NO_INTEGRATION:
      return "The Odoo integration is not configured. Please set up the Odoo integration in your settings and retry using the actions menu below.";
    case OdooIntegrationErrorCode.ODOO_AUTH_FAILED:
      return "Odoo rejected the credentials. Please verify the instance URL, database, username, and API key in your Odoo integration settings and retry using the actions menu below.";
    case OdooIntegrationErrorCode.ODOO_INVOICE_NOT_FOUND:
      return "No matching customer invoice was found in Odoo for this order. Please verify the order exists in Odoo, then retry using the actions menu below, or create the credit note manually in Odoo.";
    case OdooIntegrationErrorCode.ODOO_GENERAL_ERROR:
      return "The Odoo API returned an error. Please retry using the actions menu below, or create the credit note manually in Odoo.";
    case LinnworksIntegrationErrorCode.LINNWORKS_ORDER_NOT_FOUND:
      return "No matching Linnworks order was found for this return. Please verify the order exists in Linnworks and retry using the actions menu below, or create the RMA manually in Linnworks.";
    case LinnworksIntegrationErrorCode.LINNWORKS_ORDER_AMBIGUOUS:
      return "More than one Linnworks order matches this return's order number. Please create the RMA manually in Linnworks against the correct order, then dismiss this error.";
    case LinnworksIntegrationErrorCode.LINNWORKS_SKU_NOT_FOUND:
      return "One or more returned SKUs were not found on the Linnworks order. Please verify the SKUs match in Linnworks and retry using the actions menu below, or create the RMA manually in Linnworks.";
    case LinnworksIntegrationErrorCode.LINNWORKS_SKU_AMBIGUOUS:
      return "One or more returned SKUs appear on more than one line of the Linnworks order, so the returned line could not be identified. Please create the RMA manually in Linnworks, then dismiss this error.";
    case LinnworksIntegrationErrorCode.LINNWORKS_LOCATION_NOT_MAPPED:
      return "No Linnworks returns location applies to this return. In-store returns need their shop mapped to a Linnworks location; other returns fall back to the default location. Please update your Linnworks returns settings and retry using the actions menu below.";
    case LinnworksIntegrationErrorCode.LINNWORKS_RMA_REJECTED:
      return "Linnworks rejected the RMA booking. Please review the return in Linnworks and retry using the actions menu below, or create the RMA manually in Linnworks.";
    default:
      assertNever(errorCode);
  }
};

// Individual integration error code enums
export enum BlueCherryIntegrationErrorCode {
  BLUE_CHERRY_GENERAL_ERROR = "BLUE_CHERRY_GENERAL_ERROR",
  BLUE_CHERRY_NO_INTEGRATION = "BLUE_CHERRY_NO_INTEGRATION",
}

export enum MintsoftIntegrationErrorCode {
  MINTSOFT_GENERAL_ERROR = "MINTSOFT_GENERAL_ERROR",
  MINTSOFT_NO_INTEGRATION = "MINTSOFT_NO_INTEGRATION",
  MINTSOFT_AUTH_FAILED = "MINTSOFT_AUTH_FAILED",
  MINTSOFT_WAREHOUSE_NOT_FOUND = "MINTSOFT_WAREHOUSE_NOT_FOUND",
  MINTSOFT_CLIENT_ID_MISSING = "MINTSOFT_CLIENT_ID_MISSING",
}

export enum OdooIntegrationErrorCode {
  ODOO_GENERAL_ERROR = "ODOO_GENERAL_ERROR",
  ODOO_NO_INTEGRATION = "ODOO_NO_INTEGRATION",
  ODOO_AUTH_FAILED = "ODOO_AUTH_FAILED",
  ODOO_INVOICE_NOT_FOUND = "ODOO_INVOICE_NOT_FOUND",
}

export enum LinnworksIntegrationErrorCode {
  LINNWORKS_ORDER_NOT_FOUND = "LINNWORKS_ORDER_NOT_FOUND",
  LINNWORKS_ORDER_AMBIGUOUS = "LINNWORKS_ORDER_AMBIGUOUS",
  LINNWORKS_SKU_NOT_FOUND = "LINNWORKS_SKU_NOT_FOUND",
  LINNWORKS_SKU_AMBIGUOUS = "LINNWORKS_SKU_AMBIGUOUS",
  LINNWORKS_LOCATION_NOT_MAPPED = "LINNWORKS_LOCATION_NOT_MAPPED",
  LINNWORKS_RMA_REJECTED = "LINNWORKS_RMA_REJECTED",
}

export enum Aims360IntegrationErrorCode {
  AIMS360_GENERAL_ERROR = "AIMS360_GENERAL_ERROR",
  AIMS360_NO_INVOICES_FOUND = "AIMS360_NO_INVOICES_FOUND",
  AIMS360_NO_AVAILABLE_QUANTITY = "AIMS360_NO_AVAILABLE_QUANTITY",
}

export enum ApparelMagicIntegrationErrorCode {
  APPAREL_MAGIC_ORDER_NOT_FOUND = "APPAREL_MAGIC_ORDER_NOT_FOUND",
  APPAREL_MAGIC_RA_NOT_DEFINED = "APPAREL_MAGIC_RA_NOT_DEFINED",
  APPAREL_MAGIC_MULTIPLE_ORDERS = "APPAREL_MAGIC_MULTIPLE_ORDERS",
  APPAREL_MAGIC_NO_ITEMS_TO_RETURN = "APPAREL_MAGIC_NO_ITEMS_TO_RETURN",
  APPAREL_MAGIC_INSUFFICIENT_QUANTITY = "APPAREL_MAGIC_INSUFFICIENT_QUANTITY",
  APPAREL_MAGIC_UNAUTHORIZED_ERROR = "APPAREL_MAGIC_UNAUTHORIZED_ERROR",
}

export enum BrightpearlIntegrationErrorCode {
  BRIGHTPEARL_SALES_ORDER_ROW_NOT_FOUND = "BRIGHTPEARL_SALES_ORDER_ROW_NOT_FOUND",
  BRIGHTPEARL_ORIGINAL_SALES_ORDER_NOT_FOUND = "BRIGHTPEARL_ORIGINAL_SALES_ORDER_NOT_FOUND",
}

export enum CloudxIntegrationErrorCode {
  CLOUDX_ITEM_MASTER_ERROR = "CLOUDX_ITEM_MASTER_ERROR",
  CLOUDX_STATE_ERROR = "CLOUDX_STATE_ERROR",
  CLOUDX_UPC_SKU_NOT_FOUND = "CLOUDX_UPC_SKU_NOT_FOUND",
  CLOUDX_REFRESH_TOKEN_INVALID = "CLOUDX_REFRESH_TOKEN_INVALID",
}

export enum ExtensivIntegrationErrorCode {
  EXTENSIV_AUTHENTICATION_ERROR = "EXTENSIV_AUTHENTICATION_ERROR",
  EXTENSIV_BAD_REQUEST = "EXTENSIV_BAD_REQUEST",
  EXTENSIV_DUPLICATE_ENTRY = "EXTENSIV_DUPLICATE_ENTRY",
}

export enum InmarIntegrationErrorCode {
  INMAR_MISSING_ITEM_ID = "INMAR_MISSING_ITEM_ID",
  INMAR_NO_INTEGRATION = "INMAR_NO_INTEGRATION",
  INMAR_NO_SHIPMENTS = "INMAR_NO_SHIPMENTS",
  INMAR_NO_BOX_BARCODES = "INMAR_NO_BOX_BARCODES",
}

export enum GlobaleIntegrationErrorCode {
  GLOBALE_MISSING_ORDER_NUMBER = "GLOBALE_MISSING_ORDER_NUMBER",
  GLOBALE_MISSING_SKU = "GLOBALE_MISSING_SKU",
  GLOBALE_NO_PRODUCTS = "GLOBALE_NO_PRODUCTS",
  GLOBALE_API_FAILURE = "GLOBALE_API_FAILURE",
}

export const GLOBALE_DEFAULT_PROVIDER_CODE = "REDO";

export enum Indigo8IntegrationErrorCode {
  INDIGO8_NO_INTEGRATION = "INDIGO8_NO_INTEGRATION",
  INDIGO8_AUTHENTICATION_ERROR = "INDIGO8_AUTHENTICATION_ERROR",
  INDIGO8_VALIDATION_ERROR = "INDIGO8_VALIDATION_ERROR",
  INDIGO8_NO_RECORDS_FOUND = "INDIGO8_NO_RECORDS_FOUND",
  INDIGO8_TENANT_MISMATCH = "INDIGO8_TENANT_MISMATCH",
  INDIGO8_API_ERROR = "INDIGO8_API_ERROR",
}

export const Indigo8IntegrationErrorCodeZodSchema = createEnum(
  Indigo8IntegrationErrorCode,
  { enumName: "Indigo8IntegrationErrorCode" },
);

export enum AstroIntegrationErrorCode {
  ASTRO_NO_INTEGRATION = "ASTRO_NO_INTEGRATION",
  ASTRO_AUTHENTICATION_ERROR = "ASTRO_AUTHENTICATION_ERROR",
  ASTRO_VALIDATION_ERROR = "ASTRO_VALIDATION_ERROR",
  ASTRO_API_ERROR = "ASTRO_API_ERROR",
}

export const AstroIntegrationErrorCodeZodSchema = createEnum(
  AstroIntegrationErrorCode,
  { enumName: "AstroIntegrationErrorCode" },
);

export enum ControlPortIntegrationErrorCode {
  CONTROLPORT_NO_INTEGRATION = "CONTROLPORT_NO_INTEGRATION",
  CONTROLPORT_AUTHENTICATION_ERROR = "CONTROLPORT_AUTHENTICATION_ERROR",
  CONTROLPORT_VALIDATION_ERROR = "CONTROLPORT_VALIDATION_ERROR",
  CONTROLPORT_NO_RECORDS_FOUND = "CONTROLPORT_NO_RECORDS_FOUND",
  CONTROLPORT_API_ERROR = "CONTROLPORT_API_ERROR",
}

export const ControlPortIntegrationErrorCodeZodSchema = createEnum(
  ControlPortIntegrationErrorCode,
  { enumName: "ControlPortIntegrationErrorCode" },
);

export enum FlexportIntegrationErrorCode {
  FLEXPORT_TRACKING_CODE_NOT_FOUND = "FLEXPORT_TRACKING_CODE_NOT_FOUND",
  FLEXPORT_CARRIER_NOT_FOUND = "FLEXPORT_CARRIER_NOT_FOUND",
  FLEXPORT_INVALID_LOCATION = "FLEXPORT_INVALID_LOCATION",
  FLEXPORT_DUPLICATE_ENTRY = "FLEXPORT_DUPLICATE_ENTRY",
  FLEXPORT_PRODUCT_NOT_FOUND = "FLEXPORT_PRODUCT_NOT_FOUND",
  FLEXPORT_BAD_REQUEST = "FLEXPORT_BAD_REQUEST",
  FLEXPORT_TOKEN_INVALID = "FLEXPORT_TOKEN_INVALID",
}

export enum NetsuiteIntegrationErrorCode {
  NETSUITE_AMBIGUOUS_ORDER = "NETSUITE_AMBIGUOUS_ORDER",
  NETSUITE_SALES_ORDER_NOT_FOUND = "NETSUITE_SALES_ORDER_NOT_FOUND",
  NETSUITE_RETURN_AUTHORIZATION_CANNOT_BE_INITIALIZED = "NETSUITE_RETURN_AUTHORIZATION_CANNOT_BE_INITIALIZED",
  NETSUITE_RETURN_AUTHORIZATION_MUST_HAVE_LINE_ITEMS = "NETSUITE_RETURN_AUTHORIZATION_MUST_HAVE_LINE_ITEMS",
  NETSUITE_END_OF_GROUP_LINE_DELETE_ERROR = "NETSUITE_END_OF_GROUP_LINE_DELETE_ERROR",
  NETSUITE_THIS_RECORD_ALREADY_EXISTS_ERROR = "NETSUITE_THIS_RECORD_ALREADY_EXISTS_ERROR",
  NETSUITE_INVALID_FIELD_VALUE_ERROR = "NETSUITE_INVALID_FIELD_VALUE_ERROR",
  NETSUITE_INVALID_LOCATION = "NETSUITE_INVALID_LOCATION",
  NETSUITE_INVALID_LOGIN_ATTEMPT_ERROR = "NETSUITE_INVALID_LOGIN_ATTEMPT_ERROR",
  NETSUITE_UNEXPECTED_SUITE_SCRIPT_ERROR = "NETSUITE_UNEXPECTED_SUITE_SCRIPT_ERROR",
  NETSUITE_SALES_ORDER_MUST_BE_FULFILLED_ERROR = "NETSUITE_SALES_ORDER_MUST_BE_FULFILLED_ERROR",
  NETSUITE_PERMISSION_VIOLATION_LISTS_TAX = "NETSUITE_PERMISSION_VIOLATION_LISTS_TAX",
  NETSUITE_INVENTORY_DETAIL_QUANTITY_ERROR = "NETSUITE_INVENTORY_DETAIL_QUANTITY_ERROR",
  NETSUITE_ITEM_NOT_FOUND_FOR_SKU = "NETSUITE_ITEM_NOT_FOUND_FOR_SKU",
  NETSUITE_CUSTOMER_RESOLUTION_FAILED = "NETSUITE_CUSTOMER_RESOLUTION_FAILED",
  NETSUITE_RMA_SOURCE_UNAVAILABLE = "NETSUITE_RMA_SOURCE_UNAVAILABLE",
}

export enum SfomIntegrationErrorCode {
  SFOM_DIDNT_RECEIVE_RMA_ID = "SFOM_DIDNT_RECEIVE_RMA_ID",
}

export enum ShipedgeIntegrationErrorCode {
  SHIPEDGE_DUPLICATE_ENTRY = "SHIPEDGE_DUPLICATE_ENTRY",
  SHIPEDGE_BAD_REQUEST = "SHIPEDGE_BAD_REQUEST",
  SHIPEDGE_AUTHENTICATION_ERROR = "SHIPEDGE_AUTHENTICATION_ERROR",
  SHIPEDGE_ORDER_NOT_FOUND = "SHIPEDGE_ORDER_NOT_FOUND",
}

export enum SellercloudIntegrationErrorCode {
  SELLERCLOUD_NO_INTEGRATION = "SELLERCLOUD_NO_INTEGRATION",
  SELLERCLOUD_AUTHENTICATION_ERROR = "SELLERCLOUD_AUTHENTICATION_ERROR",
  SELLERCLOUD_NO_ORDERS_FOUND = "SELLERCLOUD_NO_ORDERS_FOUND",
  SELLERCLOUD_PERMISSION_DENIED = "SELLERCLOUD_PERMISSION_DENIED",
  SELLERCLOUD_NO_RETURN_ID = "SELLERCLOUD_NO_RETURN_ID",
  SELLERCLOUD_API_ERROR = "SELLERCLOUD_API_ERROR",
}

export enum RyderIntegrationErrorCode {
  RYDER_NO_INTEGRATION = "RYDER_NO_INTEGRATION",
  RYDER_MISSING_REFRESH_TOKEN = "RYDER_MISSING_REFRESH_TOKEN",
  RYDER_REFRESH_TOKEN_INVALID = "RYDER_REFRESH_TOKEN_INVALID",
  RYDER_NO_ORDERS_FOUND = "RYDER_NO_ORDERS_FOUND",
  RYDER_NO_WAREHOUSES = "RYDER_NO_WAREHOUSES",
  RYDER_WAREHOUSE_NOT_MATCHED = "RYDER_WAREHOUSE_NOT_MATCHED",
  RYDER_NO_RETURN_ID = "RYDER_NO_RETURN_ID",
  RYDER_DUPLICATE_REFERENCE = "RYDER_DUPLICATE_REFERENCE",
  RYDER_NO_RETURN_ITEMS = "RYDER_NO_RETURN_ITEMS",
}

export enum Cin7OmniIntegrationErrorCode {
  CIN7_OMNI_NO_INTEGRATION = "CIN7_OMNI_NO_INTEGRATION",
  CIN7_OMNI_INVALID_ORDER = "CIN7_OMNI_INVALID_ORDER",
  CIN7_OMNI_NO_ORDERS_FOUND = "CIN7_OMNI_NO_ORDERS_FOUND",
  CIN7_OMNI_NO_RETURN_ID = "CIN7_OMNI_NO_RETURN_ID",
  CIN7_OMNI_NO_RETURN_ITEMS = "CIN7_OMNI_NO_RETURN_ITEMS",
  CIN7_OMNI_API_ERROR = "CIN7_OMNI_API_ERROR",
}

export enum ShipbobIntegrationErrorCode {
  SHIPBOB_NO_ORDERS_FOUND_FOR_SHOPIFY_ORDER_ERROR = "SHIPBOB_NO_ORDERS_FOUND_FOR_SHOPIFY_ORDER_ERROR",
  SHIPBOB_NO_FULFILLMENT_CENTERS = "SHIPBOB_NO_FULFILLMENT_CENTERS",
  SHIPBOB_NO_REDO_CHANNEL = "SHIPBOB_NO_REDO_CHANNEL",
  SHIPBOB_NO_REFERENCE_IDS = "SHIPBOB_NO_REFERENCE_IDS",
  SHIPBOB_DUPLICATE_REFERENCE_ID = "SHIPBOB_DUPLICATE_REFERENCE_ID",
  SHIPBOB_MISSING_REFRESH_TOKEN = "SHIPBOB_MISSING_REFRESH_TOKEN",
  SHIPBOB_REFRESH_TOKEN_INVALID = "SHIPBOB_REFRESH_TOKEN_INVALID",
  SHIPBOB_NO_INTEGRATION = "SHIPBOB_NO_INTEGRATION",
  SHIPBOB_NO_RETURN_ID = "SHIPBOB_NO_RETURN_ID",
  SHIPBOB_NO_RETURN_FOUND_BY_REFERENCE_ID = "SHIPBOB_NO_RETURN_FOUND_BY_REFERENCE_ID",
  SHIPBOB_FULFILLMENT_CENTER_NOT_MATCHED = "SHIPBOB_FULFILLMENT_CENTER_NOT_MATCHED",
}

export enum DeposcoIntegrationErrorCode {
  NO_INTEGRATION = "DEPOSCO_NO_INTEGRATION",
  NO_ORDER = "DEPOSCO_NO_ORDER",
  NO_FACILITY_MATCH = "DEPOSCO_NO_FACILITY_MATCH",
  API_ERROR = "DEPOSCO_API_ERROR",
  REFRESH_TOKEN_INVALID = "DEPOSCO_REFRESH_TOKEN_INVALID",
}

export enum EcwidIntegrationErrorCode {
  ECWID_NO_INTEGRATION = "ECWID_NO_INTEGRATION",
  ECWID_MISSING_SHIPPING_ADDRESS = "ECWID_MISSING_SHIPPING_ADDRESS",
  ECWID_NO_ITEMS_TO_SUBMIT = "ECWID_NO_ITEMS_TO_SUBMIT",
  ECWID_AUTHENTICATION_ERROR = "ECWID_AUTHENTICATION_ERROR",
  ECWID_REQUEST_FAILED = "ECWID_REQUEST_FAILED",
  ECWID_INVALID_RESPONSE = "ECWID_INVALID_RESPONSE",
}

export enum MasonhubIntegrationErrorCode {
  MASONHUB_NO_INTEGRATION = "MASONHUB_NO_INTEGRATION",
  MASONHUB_AUTHENTICATION_ERROR = "MASONHUB_AUTHENTICATION_ERROR",
  MASONHUB_BAD_REQUEST = "MASONHUB_BAD_REQUEST",
  MASONHUB_DUPLICATE_ENTRY = "MASONHUB_DUPLICATE_ENTRY",
  MASONHUB_SKU_NOT_FOUND = "MASONHUB_SKU_NOT_FOUND",
  MASONHUB_NO_ITEMS = "MASONHUB_NO_ITEMS",
  MASONHUB_API_ERROR = "MASONHUB_API_ERROR",
}

export enum ShipmonkIntegrationErrorCode {
  SHIPMONK_NO_INTEGRATION = "SHIPMONK_NO_INTEGRATION",
  SHIPMONK_DUPLICATE_ENTRY = "SHIPMONK_DUPLICATE_ENTRY",
  SHIPMONK_BAD_REQUEST = "SHIPMONK_BAD_REQUEST",
  SHIPMONK_SKU_NOT_FOUND = "SHIPMONK_SKU_NOT_FOUND",
  SHIPMONK_WAREHOUSE_NOT_CONFIGURED = "SHIPMONK_WAREHOUSE_NOT_CONFIGURED",
  SHIPMONK_STORE_NOT_CONFIGURED = "SHIPMONK_STORE_NOT_CONFIGURED",
  SHIPMONK_AUTHENTICATION_ERROR = "SHIPMONK_AUTHENTICATION_ERROR",
  SHIPMONK_NO_WAREHOUSES = "SHIPMONK_NO_WAREHOUSES",
  SHIPMONK_WAREHOUSE_NOT_MATCHED = "SHIPMONK_WAREHOUSE_NOT_MATCHED",
  SHIPMONK_ORDER_NOT_FOUND = "SHIPMONK_ORDER_NOT_FOUND",
}

export enum ShipheroIntegrationErrorCode {
  SHIPHERO_ORDER_NOT_FOUND = "SHIPHERO_ORDER_NOT_FOUND",
  SHIPHERO_SKU_MISSING_ON_ORDER = "SHIPHERO_SKU_MISSING_ON_ORDER",
  SHIPHERO_ACCOUNT_CANCELED = "SHIPHERO_ACCOUNT_CANCELED",
  SHIPHERO_NO_ACCESS_TOKEN = "SHIPHERO_NO_ACCESS_TOKEN",
}

export enum TrackstarIntegrationErrorCode {
  SHIPSTREAM_DELIVERY_ALREADY_EXISTS = "SHIPSTREAM_DELIVERY_ALREADY_EXISTS",
  SHIPSTREAM_SKU_NOT_FOUND = "SHIPSTREAM_SKU_NOT_FOUND",
  ORDER_ID_NOT_FOUND_AFTER_CALLING_TRACKSTAR_ORDER_API = "ORDER_ID_NOT_FOUND_AFTER_CALLING_TRACKSTAR_ORDER_API",
  ORDER_NOT_FOUND = "ORDER_NOT_FOUND",
  ORDER_ITEM_NOT_FOUND = "ORDER_ITEM_NOT_FOUND",
  WHIPLASH_RYDER_INVENTORY_ITEM_NOT_FOUND = "WHIPLASH_RYDER_INVENTORY_ITEM_NOT_FOUND",
  WHIPLASH_RYDER_SKU_NOT_FOUND = "WHIPLASH_RYDER_SKU_NOT_FOUND",
  RETURN_ALREADY_EXISTS = "RETURN_ALREADY_EXISTS",
  NO_WAREHOUSE_FOUND_FOR_SHIPMENT = "NO_WAREHOUSE_FOUND_FOR_SHIPMENT",
  NO_ORDER_ID_FOUND_FOR_RETURN = "NO_ORDER_ID_FOUND_FOR_RETURN",
  ORDER_NOT_FOUND_FOR_CUSTOMER_NUMBER_OR_PO_NUMBER = "ORDER_NOT_FOUND_FOR_CUSTOMER_NUMBER_OR_PO_NUMBER",
  RYDER_ORDER_NOT_YET_SHIPPED = "RYDER_ORDER_NOT_YET_SHIPPED",
  DEPOSCO_DUPLICATE_ENTRY = "DEPOSCO_DUPLICATE_ENTRY",
  DEPOSCO_ORDER_NOT_FOUND = "DEPOSCO_ORDER_NOT_FOUND",
  DEPOSCO_INVALID_CREDENTIALS = "DEPOSCO_INVALID_CREDENTIALS",
}

export enum GeneralErrorCode {
  LABEL_REQUIRED = "LABEL_REQUIRED",
  RMA_FUNCTION_FAILED = "RMA_FUNCTION_FAILED",
}

export enum UnknownErrorCode {
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
}

export enum ReturnIntegrationSyncResultType {
  NoSetUp = "NO_SET_UP",
  Failure = "FAILURE",
  AlreadyExists = "ALREADY_EXISTS",
  LogicallyNotNeeded = "LOGICALLY_NOT_NEEDED",
  LabelRequired = "LABEL_REQUIRED",
  Success = "SUCCESS",
}

// Zod schemas for individual integration error codes
export const BlueCherryIntegrationErrorCodeZodSchema = createEnum(
  BlueCherryIntegrationErrorCode,
  { enumName: "BlueCherryIntegrationErrorCode" },
);
export const MintsoftIntegrationErrorCodeZodSchema = createEnum(
  MintsoftIntegrationErrorCode,
  { enumName: "MintsoftIntegrationErrorCode" },
);
export const OdooIntegrationErrorCodeZodSchema = createEnum(
  OdooIntegrationErrorCode,
  { enumName: "OdooIntegrationErrorCode" },
);
export const LinnworksIntegrationErrorCodeZodSchema = createEnum(
  LinnworksIntegrationErrorCode,
  { enumName: "LinnworksIntegrationErrorCode" },
);
export const Aims360IntegrationErrorCodeZodSchema = createEnum(
  Aims360IntegrationErrorCode,
  { enumName: "Aims360IntegrationErrorCode" },
);
export const ApparelMagicIntegrationErrorCodeZodSchema = createEnum(
  ApparelMagicIntegrationErrorCode,
  { enumName: "ApparelMagicIntegrationErrorCode" },
);
export const BrightpearlIntegrationErrorCodeZodSchema = createEnum(
  BrightpearlIntegrationErrorCode,
  { enumName: "BrightpearlIntegrationErrorCode" },
);
export const CloudxIntegrationErrorCodeZodSchema = createEnum(
  CloudxIntegrationErrorCode,
  { enumName: "CloudxIntegrationErrorCode" },
);
export const ExtensivIntegrationErrorCodeZodSchema = createEnum(
  ExtensivIntegrationErrorCode,
  { enumName: "ExtensivIntegrationErrorCode" },
);
export const InmarIntegrationErrorCodeZodSchema = createEnum(
  InmarIntegrationErrorCode,
  { enumName: "InmarIntegrationErrorCode" },
);
export const GlobaleIntegrationErrorCodeZodSchema = createEnum(
  GlobaleIntegrationErrorCode,
  { enumName: "GlobaleIntegrationErrorCode" },
);
export const FlexportIntegrationErrorCodeZodSchema = createEnum(
  FlexportIntegrationErrorCode,
  { enumName: "FlexportIntegrationErrorCode" },
);
export const NetsuiteIntegrationErrorCodeZodSchema = createEnum(
  NetsuiteIntegrationErrorCode,
  { enumName: "NetsuiteIntegrationErrorCode" },
);
export const SfomIntegrationErrorCodeZodSchema = createEnum(
  SfomIntegrationErrorCode,
  { enumName: "SfomIntegrationErrorCode" },
);
export const ShipedgeIntegrationErrorCodeZodSchema = createEnum(
  ShipedgeIntegrationErrorCode,
  { enumName: "ShipedgeIntegrationErrorCode" },
);
export const RyderIntegrationErrorCodeZodSchema = createEnum(
  RyderIntegrationErrorCode,
  { enumName: "RyderIntegrationErrorCode" },
);
export const SellercloudIntegrationErrorCodeZodSchema = createEnum(
  SellercloudIntegrationErrorCode,
  { enumName: "SellercloudIntegrationErrorCode" },
);
export const ShipbobIntegrationErrorCodeZodSchema = createEnum(
  ShipbobIntegrationErrorCode,
  { enumName: "ShipbobIntegrationErrorCode" },
);
export const Cin7OmniIntegrationErrorCodeZodSchema = createEnum(
  Cin7OmniIntegrationErrorCode,
  { enumName: "Cin7OmniIntegrationErrorCode" },
);
export const MasonhubIntegrationErrorCodeZodSchema = createEnum(
  MasonhubIntegrationErrorCode,
  { enumName: "MasonhubIntegrationErrorCode" },
);
export const ShipmonkIntegrationErrorCodeZodSchema = createEnum(
  ShipmonkIntegrationErrorCode,
  { enumName: "ShipmonkIntegrationErrorCode" },
);
export const EcwidIntegrationErrorCodeZodSchema = createEnum(
  EcwidIntegrationErrorCode,
  { enumName: "EcwidIntegrationErrorCode" },
);
export const DeposcoIntegrationErrorCodeZodSchema = createEnum(
  DeposcoIntegrationErrorCode,
  { enumName: "DeposcoIntegrationErrorCode" },
);
export const ShipheroIntegrationErrorCodeZodSchema = createEnum(
  ShipheroIntegrationErrorCode,
  { enumName: "ShipheroIntegrationErrorCode" },
);
export const TrackstarIntegrationErrorCodeZodSchema = createEnum(
  TrackstarIntegrationErrorCode,
  { enumName: "TrackstarIntegrationErrorCode" },
);
export const GeneralErrorCodeZodSchema = createEnum(GeneralErrorCode, {
  enumName: "GeneralErrorCode",
});
export const UnknownErrorCodeZodSchema = createEnum(UnknownErrorCode, {
  enumName: "UnknownErrorCode",
});

// Union type for all integration error codes
export type ReturnIntegrationErrorCode =
  | BlueCherryIntegrationErrorCode
  | MintsoftIntegrationErrorCode
  | ShipheroIntegrationErrorCode
  | BrightpearlIntegrationErrorCode
  | DeposcoIntegrationErrorCode
  | FlexportIntegrationErrorCode
  | InmarIntegrationErrorCode
  | NetsuiteIntegrationErrorCode
  | RyderIntegrationErrorCode
  | SellercloudIntegrationErrorCode
  | ShipbobIntegrationErrorCode
  | Cin7OmniIntegrationErrorCode
  | ShipmonkIntegrationErrorCode
  | MasonhubIntegrationErrorCode
  | EcwidIntegrationErrorCode
  | ShipedgeIntegrationErrorCode
  | SfomIntegrationErrorCode
  | ApparelMagicIntegrationErrorCode
  | Aims360IntegrationErrorCode
  | CloudxIntegrationErrorCode
  | ExtensivIntegrationErrorCode
  | TrackstarIntegrationErrorCode
  | GlobaleIntegrationErrorCode
  | Indigo8IntegrationErrorCode
  | AstroIntegrationErrorCode
  | ControlPortIntegrationErrorCode
  | OdooIntegrationErrorCode
  | LinnworksIntegrationErrorCode
  | GeneralErrorCode
  | UnknownErrorCode;

// Zod schema for the union type
export const ReturnIntegrationErrorCodeZodSchema = z.union([
  BlueCherryIntegrationErrorCodeZodSchema,
  MintsoftIntegrationErrorCodeZodSchema,
  ShipheroIntegrationErrorCodeZodSchema,
  DeposcoIntegrationErrorCodeZodSchema,
  BrightpearlIntegrationErrorCodeZodSchema,
  FlexportIntegrationErrorCodeZodSchema,
  InmarIntegrationErrorCodeZodSchema,
  NetsuiteIntegrationErrorCodeZodSchema,
  RyderIntegrationErrorCodeZodSchema,
  SellercloudIntegrationErrorCodeZodSchema,
  ShipbobIntegrationErrorCodeZodSchema,
  Cin7OmniIntegrationErrorCodeZodSchema,
  ShipmonkIntegrationErrorCodeZodSchema,
  MasonhubIntegrationErrorCodeZodSchema,
  EcwidIntegrationErrorCodeZodSchema,
  ShipedgeIntegrationErrorCodeZodSchema,
  SfomIntegrationErrorCodeZodSchema,
  ApparelMagicIntegrationErrorCodeZodSchema,
  Aims360IntegrationErrorCodeZodSchema,
  CloudxIntegrationErrorCodeZodSchema,
  ExtensivIntegrationErrorCodeZodSchema,
  TrackstarIntegrationErrorCodeZodSchema,
  GlobaleIntegrationErrorCodeZodSchema,
  Indigo8IntegrationErrorCodeZodSchema,
  AstroIntegrationErrorCodeZodSchema,
  ControlPortIntegrationErrorCodeZodSchema,
  OdooIntegrationErrorCodeZodSchema,
  LinnworksIntegrationErrorCodeZodSchema,
  GeneralErrorCodeZodSchema,
  UnknownErrorCodeZodSchema,
]);

export const ReturnIntegrationErrorCodeValues = [
  ...Object.values(BlueCherryIntegrationErrorCode),
  ...Object.values(MintsoftIntegrationErrorCode),
  ...Object.values(ShipheroIntegrationErrorCode),
  ...Object.values(DeposcoIntegrationErrorCode),
  ...Object.values(BrightpearlIntegrationErrorCode),
  ...Object.values(FlexportIntegrationErrorCode),
  ...Object.values(InmarIntegrationErrorCode),
  ...Object.values(NetsuiteIntegrationErrorCode),
  ...Object.values(RyderIntegrationErrorCode),
  ...Object.values(SellercloudIntegrationErrorCode),
  ...Object.values(ShipbobIntegrationErrorCode),
  ...Object.values(Cin7OmniIntegrationErrorCode),
  ...Object.values(ShipmonkIntegrationErrorCode),
  ...Object.values(MasonhubIntegrationErrorCode),
  ...Object.values(EcwidIntegrationErrorCode),
  ...Object.values(ShipedgeIntegrationErrorCode),
  ...Object.values(SfomIntegrationErrorCode),
  ...Object.values(ApparelMagicIntegrationErrorCode),
  ...Object.values(Aims360IntegrationErrorCode),
  ...Object.values(CloudxIntegrationErrorCode),
  ...Object.values(ExtensivIntegrationErrorCode),
  ...Object.values(TrackstarIntegrationErrorCode),
  ...Object.values(GlobaleIntegrationErrorCode),
  ...Object.values(Indigo8IntegrationErrorCode),
  ...Object.values(AstroIntegrationErrorCode),
  ...Object.values(ControlPortIntegrationErrorCode),
  ...Object.values(OdooIntegrationErrorCode),
  ...Object.values(LinnworksIntegrationErrorCode),
  ...Object.values(GeneralErrorCode),
  ...Object.values(UnknownErrorCode),
];
export type ReturnIntegrationErrorCodeValues =
  (typeof ReturnIntegrationErrorCodeValues)[number];

export const ReturnIntegrationSyncResultTypeZodSchema = createEnum(
  ReturnIntegrationSyncResultType,
  { enumName: "ReturnIntegrationSyncResultType" },
);

// Following the pattern from the codebase where we have both the underscore version and the typed version
const ReturnIntegrationErrorCodeZodSchema_ =
  ReturnIntegrationErrorCodeZodSchema;
export const ReturnIntegrationErrorCodeZodSchemaFinal: z.ZodType<ReturnIntegrationErrorCode> =
  ReturnIntegrationErrorCodeZodSchema_;
export type ReturnIntegrationErrorCodeZod = z.infer<
  typeof ReturnIntegrationErrorCodeZodSchema_
>;

export enum NetSuiteTransactionType {
  CASH_SALE = "cash_sale",
  SALES_ORDER = "sales_order",
  BOTH = "both",
}

export const NetSuiteTransactionTypeZodSchema = createEnum(
  NetSuiteTransactionType,
  { enumName: "NetSuiteTransactionType" },
);

export enum NetSuiteAuthMethod {
  TBA = "tba",
  OAUTH2 = "oauth2",
}

export const NetSuiteAuthMethodZodSchema = createEnum(NetSuiteAuthMethod, {
  enumName: "NetSuiteAuthMethod",
});

export enum NetSuiteItemValueType {
  CREDIT = "credit",
  REFUND = "refund",
  TOTAL = "total",
  TOTAL_PRE_DISCOUNT = "total-pre-discount",
}

export const NetSuiteItemValueTypeZodSchema = createEnum(
  NetSuiteItemValueType,
  { enumName: "NetSuiteItemValueType" },
);
